/**
 * 模块: 单指标配置面板
 * 职责: 显示和调整单个结果指标的子维度权重
 * 限制: <200行
 */

import React from 'react';
import { Slider, Row, Col, Statistic, Alert, Tag, Divider } from 'antd';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import type { MetricConfig } from '../../../services/configPresetAPI';
import { getComponentLabel } from '../constants/componentLabels';

interface MetricConfigPanelProps {
  metricKey: string;
  metricConfig: MetricConfig;
  onChange: (newConfig: MetricConfig) => void;
  color?: string;
}

// ============================================================================
// 主组件
// ============================================================================

export const MetricConfigPanel: React.FC<MetricConfigPanelProps> = ({
  metricKey,
  metricConfig,
  onChange,
  color = '#1890ff',
}) => {
  // ============================================================================
  // 权重计算
  // ============================================================================

  const components = metricConfig.components || [];
  const totalWeight = components.reduce((sum, comp) => sum + comp.weight, 0);
  const isBalanced = totalWeight === 100;

  // ============================================================================
  // 事件处理
  // ============================================================================

  const handleWeightChange = (componentName: string, newWeight: number) => {
    const newComponents = components.map((comp) =>
      comp.name === componentName ? { ...comp, weight: newWeight } : comp
    );

    onChange({
      ...metricConfig,
      components: newComponents,
    });
  };

  // ============================================================================
  // 渲染
  // ============================================================================

  return (
    <div className="metric-config-panel">
      {/* 权重调整区域 */}
      <div className="weights-adjust-section">
        <h3>子维度权重调整</h3>
        <p className="section-desc">
          调整各子维度的权重来改变指标的计算侧重点。权重总和必须为100%。
        </p>

        <div className="weight-items-grid">
          {components.map((component) => (
            <div key={component.name} className="weight-item-inline">
              <div className="weight-info">
                <span className="weight-label" style={{ color }}>
                  {getComponentLabel(metricKey, component.name)}
                </span>
                <Tag color={color}>{component.weight}%</Tag>
              </div>
              <Slider
                min={0}
                max={100}
                step={5}
                value={component.weight}
                onChange={(value) => handleWeightChange(component.name, value)}
                trackStyle={{ backgroundColor: color }}
                handleStyle={{ borderColor: color }}
              />
            </div>
          ))}
        </div>
      </div>

      <Divider style={{ margin: '16px 0' }} />

      {/* 权重总和验证 */}
      <Row gutter={16}>
        <Col span={12}>
          <Statistic
            title="权重总和"
            value={totalWeight}
            suffix="%"
            valueStyle={{
              color: isBalanced ? '#52c41a' : '#ff4d4f',
            }}
            prefix={isBalanced ? <CheckCircleOutlined /> : <WarningOutlined />}
          />
        </Col>
        <Col span={12}>
          <Statistic
            title="子维度数量"
            value={components.length}
            valueStyle={{ color }}
          />
        </Col>
      </Row>

      {!isBalanced && (
        <Alert
          message={`权重总和必须为100%（当前${totalWeight}%）`}
          description="请调整滑块，使所有子维度权重的总和等于100%"
          type="warning"
          showIcon
          style={{ marginTop: 12, fontSize: '13px' }}
        />
      )}

      {/* 等级阈值（如果有） */}
      {metricConfig.grade_thresholds && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <div className="grade-thresholds-section">
            <h3>评分等级阈值</h3>
            <p className="section-desc">
              根据计算出的得分，系统会自动分配等级：
            </p>
            <Row gutter={[12, 12]}>
              {Object.entries(metricConfig.grade_thresholds)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .map(([grade, threshold]) => (
                  <Col span={8} key={grade}>
                    <div className="grade-threshold-item">
                      <Tag color={color}>{grade}</Tag>
                      <span className="threshold-value">≥ {threshold}分</span>
                    </div>
                  </Col>
                ))}
            </Row>
          </div>
        </>
      )}

      {/* 策略映射（广告优先级专用） */}
      {metricConfig.strategy_mapping && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <div className="strategy-mapping-section">
            <h3>投放策略建议</h3>
            <p className="section-desc">根据评分等级的相应策略：</p>
            <div className="strategy-list">
              {Object.entries(metricConfig.strategy_mapping).map(([grade, strategy]) => (
                <div key={grade} className="strategy-item">
                  <Tag color={color} style={{ marginRight: 8 }}>
                    {grade}级
                  </Tag>
                  <span>{strategy}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

